<template>
  <UiButton
    unstyled
    type="button"
    :class="['ui-theme-toggle', { 'ui-theme-toggle--dark': !isLight }]"
    :aria-checked="!isLight"
    :aria-label="`Переключить тему: ${themeLabel}`"
    role="switch"
    @click="emit('toggle')"
  >
    <span
      class="ui-theme-toggle__thumb"
      :class="{ 'ui-theme-toggle__thumb--dark': !isLight }"
    />
    <span class="ui-theme-toggle__icon ui-theme-toggle__icon--sun">
      <Sun
        :size="16"
        :stroke-width="1.8"
        aria-hidden="true"
        class="ui-theme-toggle__icon-image"
        :class="isLight ? 'ui-theme-toggle__icon-image--active' : 'ui-theme-toggle__icon-image--muted'"
      />
    </span>
    <span class="ui-theme-toggle__icon ui-theme-toggle__icon--moon">
      <MoonStar
        :size="16"
        :stroke-width="1.9"
        aria-hidden="true"
        class="ui-theme-toggle__icon-image"
        :class="isLight ? 'ui-theme-toggle__icon-image--muted' : 'ui-theme-toggle__icon-image--active'"
      />
    </span>
  </UiButton>
</template>

<script setup lang="ts">
import { MoonStar, Sun } from 'lucide-vue-next';

import UiButton from './UiButton.vue';

const { isLight, themeLabel } = defineProps<{
  isLight: boolean;
  themeLabel: string;
}>();

const emit = defineEmits<{ (e: 'toggle'): void }>();
</script>

<style scoped>
.ui-theme-toggle {
  --toggle-width: 4.25rem;
  --toggle-height: 2.75rem;
  --toggle-pad: 0.25rem;
  --toggle-thumb: 2.05rem;
  --toggle-track-bg: linear-gradient(135deg, color-mix(in srgb, var(--ui-surface-muted) 78%, var(--ui-border) 22%), var(--ui-surface));
  --toggle-track-border: color-mix(in srgb, var(--ui-border) 75%, var(--ui-foreground) 25%);

  position: relative;
  display: inline-flex;
  justify-content: center;
  align-items: center;
  width: var(--toggle-width);
  height: var(--toggle-height);
  padding: var(--toggle-pad);
  border-radius: var(--ui-radius-full);
  border: 1px solid var(--toggle-track-border);
  background: var(--toggle-track-bg);
  cursor: pointer;
  box-shadow: var(--ui-control-shadow);
  overflow: hidden;
  transition:
    border-color 0.2s ease,
    background 0.2s ease,
    box-shadow 0.2s ease,
    transform 0.2s ease;
}

.ui-theme-toggle:hover {
  border-color: var(--ui-border-strong);
  box-shadow: var(--ui-shadow-panel);
  transform: translateY(-1px);
}

.ui-theme-toggle:active {
  transform: translateY(0);
}

.ui-theme-toggle:focus-visible {
  outline: 2px solid var(--ui-control-ring);
  outline-offset: 3px;
}

.ui-theme-toggle--dark {
  --toggle-track-bg: linear-gradient(135deg, color-mix(in srgb, var(--ui-surface-muted) 65%, var(--ui-foreground) 10%), color-mix(in srgb, var(--ui-surface) 70%, transparent));
  --toggle-track-border: color-mix(in srgb, var(--ui-border-strong) 80%, var(--ui-foreground) 20%);
}

.ui-theme-toggle__thumb {
  position: absolute;
  top: var(--toggle-pad);
  left: var(--toggle-pad);
  width: var(--toggle-thumb);
  height: var(--toggle-thumb);
  border-radius: var(--ui-radius-full);
  background:
    linear-gradient(180deg, color-mix(in srgb, var(--ui-surface) 96%, white 4%), color-mix(in srgb, var(--ui-surface-muted) 72%, var(--ui-surface) 28%));
  border: 1px solid color-mix(in srgb, var(--ui-border) 72%, var(--ui-surface));
  box-shadow:
    0 0.75rem 1.5rem color-mix(in srgb, var(--ui-foreground) 14%, transparent),
    inset 0 1px 0 color-mix(in srgb, white 38%, transparent);
  transition:
    background 0.2s ease,
    border-color 0.2s ease,
    transform 0.2s ease;
  z-index: 0;
}

.ui-theme-toggle__thumb--dark {
  transform: translateX(calc(var(--toggle-width) - var(--toggle-thumb) - var(--toggle-pad) - var(--toggle-pad)));
  background:
    linear-gradient(180deg, color-mix(in srgb, var(--ui-accent) 42%, var(--ui-surface) 58%), color-mix(in srgb, var(--ui-surface) 78%, black 22%));
  border-color: color-mix(in srgb, var(--ui-accent) 38%, var(--ui-border));
}

.ui-theme-toggle__icon {
  position: absolute;
  width: 1rem;
  height: 1rem;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  pointer-events: none;
  z-index: 1;
}

.ui-theme-toggle__icon--sun {
  left: 0.75rem;
}

.ui-theme-toggle__icon--moon {
  right: 0.75rem;
}

.ui-theme-toggle__icon-image {
  width: 1rem;
  height: 1rem;
  display: block;
  color: var(--ui-foreground-muted);
  transition:
    color 0.2s ease,
    opacity 0.2s ease,
    transform 0.2s ease;
}

.ui-theme-toggle__icon-image--muted {
  opacity: 0.45;
  filter: grayscale(0.1);
  transform: scale(0.92);
}

.ui-theme-toggle__icon-image--active {
  color: var(--ui-accent);
  opacity: 1;
  transform: scale(1.04);
}

@media (prefers-reduced-motion: reduce) {
  .ui-theme-toggle,
  .ui-theme-toggle__thumb,
  .ui-theme-toggle__icon-image {
    transition: none;
  }

  .ui-theme-toggle:hover {
    transform: none;
  }
}
</style>
